import { Box, Input, Text } from '@chakra-ui/react';
import React, { useEffect, useId, useRef, useState } from 'react';

import {
  minSearchLength,
  type CodeSearchResult,
  type CodeType
} from '../../interface/codes';
import { ADD_CODE_DIALOG as D, TRANSFER_COLORS, TX_LABEL_COLOR } from '../../styles/tokens';

export const SEARCH_LABEL: Readonly<Record<CodeType, string>> = {
  ICD: 'SEARCH ICD-10 CATALOG',
  CPT: 'SEARCH CPT CATALOG',
  MODIFIER: 'SEARCH MODIFIER CATALOG'
};

export const SEARCH_PLACEHOLDER: Readonly<Record<CodeType, string>> = {
  ICD: "Search by code or description (e.g., 'A41.51' or 'sepsis')...",
  CPT: "Search by code or description (e.g., '99232' or 'subsequent hospital')...",
  MODIFIER: "Search by code or description (e.g., '25' or 'distinct procedural service')..."
};

type Status = 'idle' | 'loading' | 'done' | 'failed';

export type CodeSearchFieldProps = {
  readonly codeType: CodeType;
  readonly onSearch?: (codeType: CodeType, term: string) => Promise<CodeSearchResult[]>;
  readonly onSelect: (result: CodeSearchResult) => void;
  readonly debounceMs?: number;
};

export const CodeSearchField: React.FC<CodeSearchFieldProps> = ({
  codeType,
  onSearch,
  onSelect,
  debounceMs = 250
}) => {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<CodeSearchResult[]>([]);
  const [resultsKey, setResultsKey] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const generation = useRef(0);
  const focusWithin = useRef(false);
  const labelId = useId();
  const listId = useId();
  const optionId = (index: number): string => `${listId}-option-${index}`;

  useEffect(() => {
    generation.current += 1;
    const mine = generation.current;
    const trimmed = term.trim();
    if (!onSearch || trimmed.length < minSearchLength(codeType)) {
      setResults([]);
      setResultsKey('');
      setStatus('idle');
      setOpen(false);
      setActiveIndex(-1);
      return undefined;
    }
    const timer = setTimeout(() => {
      setStatus('loading');
      Promise.resolve().then(() => onSearch(codeType, trimmed)).then(
        (found) => {
          if (mine !== generation.current) return;
          setResults(found);
          setResultsKey(`${codeType}:${trimmed}`);
          setStatus('done');
          setActiveIndex(-1);
          if (focusWithin.current) setOpen(true);
        },
        () => {
          if (mine !== generation.current) return;
          setResults([]);
          setResultsKey('');
          setStatus('failed');
          setActiveIndex(-1);
          if (focusWithin.current) setOpen(true);
        }
      );
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [term, codeType, onSearch, debounceMs]);

  const choose = (result: CodeSearchResult): void => {
    onSelect(result);
    setOpen(false);
    setActiveIndex(-1);
  };

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (results.length === 0 || resultsKey !== `${codeType}:${term.trim()}`) return;
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) =>
        event.key === 'ArrowDown'
          ? (current + 1) % results.length
          : current < 0
          ? results.length - 1
          : (current - 1 + results.length) % results.length
      );
      return;
    }
    if (event.key === 'Enter' && open && activeIndex >= 0) {
      const result = results[activeIndex];
      if (result) {
        event.preventDefault();
        choose(result);
      }
      return;
    }
    if (event.key === 'Escape' && open) {
      event.stopPropagation();
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  const onBlur = (event: React.FocusEvent<HTMLDivElement>): void => {
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
    focusWithin.current = false;
    setOpen(false);
    setActiveIndex(-1);
  };

  return (
    <Box
      position="relative"
      onFocus={() => {
        focusWithin.current = true;
      }}
      onBlur={onBlur}
    >
      <Text id={labelId} fontSize={D.labelSize} fontWeight={D.labelWeight} letterSpacing={D.labelSpacing} color={TX_LABEL_COLOR} mb={D.labelGap}>
        {SEARCH_LABEL[codeType]}
      </Text>
      <Input
        role="combobox"
        aria-labelledby={labelId}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        autoComplete="off"
        placeholder={SEARCH_PLACEHOLDER[codeType]}
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        onKeyDown={onKeyDown}
        h="36px" fontSize="13px" px="11px" py="9px" borderRadius={D.controlRadius} borderColor={D.rule} bg="white"
      />
      {!onSearch && (
        <Text fontSize="sm" color={TRANSFER_COLORS.inkMuted} mt={1}>
          Code search is not available here
        </Text>
      )}
      {open && (
        <Box
          id={listId}
          role="listbox"
          aria-labelledby={labelId}
          borderWidth="1px"
          borderColor={D.rule}
          borderRadius={D.controlRadius}
          mt={1}
          maxH="220px"
          overflowY="auto"
          bg="white"
        >
          {status === 'failed' && (
            <Text role="alert" px={3} py={2} color={TRANSFER_COLORS.error}>
              Search failed
            </Text>
          )}
          {status === 'done' && results.length === 0 && (
            <Text px={3} py={2} color={TRANSFER_COLORS.inkMuted}>
              No results
            </Text>
          )}
          {results.map((result, index) => (
            <Box
              key={result.code}
              id={optionId(index)}
              role="option"
              aria-selected={index === activeIndex}
              px={3}
              py={2}
              cursor="pointer"
              bg={index === activeIndex ? TRANSFER_COLORS.optionActiveBg : 'white'}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => choose(result)}
            >
              <Text as="span" fontFamily="mono" fontWeight="semibold" mr={3}>
                {result.code}
              </Text>
              <Text as="span">{result.description}</Text>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
